// Screen 4 (spec 13 §Screens): a DE's one page (`/agents/de/{id}`; UX pass 1.2, 2026-10-27: the
// KPIs scorecard twin `/kpis/de/{id}` and the edit screen `/agents/de/{id}/edit` redirect here).
// After Lindy's and Linear's agent pages: a 56 px location header (avatar, name, seat, readiness;
// Pause / Resume, Add to channel, Message, Edit — Setup's Job section — and a "…" menu with Copy
// link), one line of facts (supervisor, template, started, deployment, band), then six tabs
// (DE_AREAS below): Record (AGENT-RECORD: the track record, a live DE's default), Overview (with
// the approvals waiting), Activity (runs, history, time, cost),
// KPIs (KPI-ASSIGN's SeatKpisPanel, the week's scorecard for KPI readers, and the targets and
// reporting), Knowledge, Setup (every configuration section, the old edit screen's steps among
// them, on one page with an "On this page" list). With the `agents` add-on off, runs and approvals
// say in one line that they fill once Agents is on, under the add-on banner (no sample rows).
// On load: the profile plus the open tab's sections' calls. `?tab=` names a section (its tab
// opens and it scrolls into view); with `kpi` off the scorecard says "not enabled" and calls no
// `kpi` route (ADR-0016).

import { ApiError } from "@companyos/sdk";
import {
  AvatarTile,
  Badge,
  BandBadge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LocationHeader,
  TabsContent,
  toast,
  ViewTabs,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  LinkIcon,
  MailIcon,
  MessageSquareIcon,
  MoreHorizontalIcon,
  PencilIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { modeAccess, openOnly, rulesByKey } from "../../extensions/modes";
import { keepFocus } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { NoAccessPage, NotEnabledPage, NotFoundPage } from "../../pages/status-pages";
import { AgentsOffBanner, useMyMembershipId, useOpenDm } from "../agents/agents-ux";
import { DeBudgetTab } from "../agents/budgets-screen";
import { DeApprovalsOff, DeRunsOff, DeSkillsOff, DeSkillsTab } from "../agents/de-profile-tabs";
import { DeAgentsTab, DeApprovalsTab, DeRunsTab } from "../agents/de-tabs";
import { DeHelpersTab, helpersSkillsText, skillTotal, useHelpers } from "../agents/helpers";
import { KnowledgeTab } from "../agents/knowledge-tab";
import { BriefSettingsModal } from "../briefs/brief-settings-modal";
import { SeatReportsToButton } from "../chart/reports-to-dialog";
import { type CompanyAccess, isManager } from "../company";
import { DeConnectionsTab } from "../connections/de-connections";
import { formatDate } from "../format";
import { NotifyLevelMenu } from "../notifications/notify-level-menu";
import { OpenAigentpanelButton, useOpenAigentpanel } from "../operator/open-aigentpanel";
import { deQuery } from "../queries";
import { LoadError, Loading, usePeriod } from "../screen-parts";
import { useFeature, useSession } from "../use-feature";
import { DeSetupPart } from "./add-de/edit-de";
import { AddToChannelButton } from "./channels-section";
import { CostTab } from "./cost-tab";
import { DeProfileSection, RuntimeLine } from "./de-about";
import { MOVES, useLifecycle } from "./de-lifecycle";
import { deLabel } from "./de-name";
import { DE_STATUS_LABELS } from "./de-status";
import { DeJobTab } from "./job-tab";
import { KeysTab } from "./keys-tab";
import { DeKpisSlot } from "./kpis-slot";
import { DeLiveBadge, liveness } from "./liveness";
import { OverviewTab } from "./overview-tab";
import { DePermissionsTab } from "./permissions-tab";
import { ReachSection } from "./reach-section";
import { RecordTab } from "./record-tab";
import { RenameDeDialog } from "./rename-de-dialog";
import { DeProbationBadge, DeRoleAccess } from "./role-access";
import { ScorecardTab } from "./scorecard-tab";
import { DeSettingsTab } from "./settings-tab";
import { DeSubscriptionsTab } from "./subscriptions-tab";
import { TimeTab } from "./time-tab";
import { deployedUnverified, pilotNote, UnverifiedBadge } from "./unverified-badge";
import { WhenNear } from "./when-near";

export const DE_TABS = [
  // AGENT-RECORD (decision 2): the track record, the page's first tab.
  "record",
  "runs",
  "approvals",
  "knowledge",
  "skills",
  "connections",
  "budget",
  "settings",
  "time",
  "cost",
  "keys",
  "subscriptions",
  "agents",
  "timeline",
  "overview",
  "scorecard",
  // AGENT-MENTION (review #13): the job configuration (standing orders) and the KPI slot.
  "job",
  "kpis",
  // D10 scope editor: Setup › Permissions.
  "permissions",
  // REACH-S1: Setup › Reach (serves, hears from, talks to, who sees).
  "reach",
  // UX pass 1.2: the old edit screen's KPI targets and reporting, channels, and review.
  "reporting",
  "channels",
  "deploy",
  // WF-POLISH: role, goal, backstory and the profile's standing orders (Setup's first section).
  "profile",
  // HELPERS: the agents (AG) that report to it, one card each.
  "helpers",
] as const;
export type DeTab = (typeof DE_TABS)[number];

/**
 * Older section names that are now part of another section (`?tab=keys` still lands). WF-POLISH
 * (Andrei, 2026-10-27): the raw History list repeated the runs in jargon; `?tab=timeline` opens
 * the runs.
 */
const SECTION_OF: Partial<Record<DeTab, DeTab>> = { keys: "connections", timeline: "runs" };
const sectionOf = (tab: DeTab): DeTab => SECTION_OF[tab] ?? tab;

/** A section's heading on its tab (the URL's `?tab=` names the section). */
const SECTION_LABELS: Record<DeTab, string> = {
  record: "Record",
  runs: "Runs",
  approvals: "Approvals waiting",
  knowledge: "Sources",
  skills: "Skills",
  connections: "Connections & keys",
  budget: "Models & budget",
  settings: "Identity & runtime",
  time: "Time",
  cost: "Cost",
  keys: "Connections & keys",
  subscriptions: "Subscription",
  agents: "Composition",
  timeline: "History",
  overview: "Overview",
  scorecard: "Scorecard",
  job: "Job & standing orders",
  kpis: "Seat KPIs",
  permissions: "Permissions",
  reach: "Reach",
  reporting: "Targets & reporting",
  channels: "Channels & approval",
  deploy: "Deploy",
  profile: "Role & standing orders",
  helpers: "Helpers",
};

/**
 * UX pass 1.2 (2026-10-27; was AGENT-MENTION's six areas with chips inside, plus a separate edit
 * screen and a scorecard twin in KPIs): one page, five tabs, no tabs inside tabs. Each tab is one
 * scrolling page of its sections, stacked full width (WF-POLISH: no "On this page" rail; the tabs
 * are enough). The URL keeps naming the section (`?tab=settings` opens Setup at Identity &
 * runtime), so every older link lands where it did.
 */
export const DE_AREAS = [
  // AGENT-RECORD (docs/direction/2026-10-28-managing-agents-gaps.md decision 2): first.
  { key: "record", label: "Record", sections: ["record"] },
  { key: "overview", label: "Overview", sections: ["overview", "approvals"] },
  { key: "activity", label: "Activity", sections: ["runs", "time", "cost"] },
  // HELPERS: only for a DE with helpers (a DE without any looks as before).
  { key: "helpers", label: "Helpers", sections: ["helpers"] },
  { key: "kpis", label: "KPIs", sections: ["kpis", "scorecard", "reporting"] },
  { key: "knowledge", label: "Knowledge", sections: ["knowledge", "skills"] },
  {
    key: "setup",
    label: "Setup",
    sections: [
      "profile",
      "deploy",
      "job",
      "settings",
      "reach",
      "permissions",
      "budget",
      "connections",
      "channels",
      "subscriptions",
      "agents",
    ],
  },
] as const satisfies readonly { key: string; label: string; sections: readonly DeTab[] }[];
type DeArea = (typeof DE_AREAS)[number];

/** The tab a section is on. */
export function areaOf(tab: DeTab): DeArea {
  const s = sectionOf(tab);
  return DE_AREAS.find((a) => (a.sections as readonly DeTab[]).includes(s)) ?? DE_AREAS[0];
}

/** Sections only managers get (their routes are manager-only), named for the no-access page. */
const MANAGER_TABS: readonly DeTab[] = [
  "budget",
  "settings",
  "keys",
  "subscriptions",
  "deploy",
  "channels",
  "reporting",
];
const MANAGER_TAB_NAMES: Partial<Record<DeTab, string>> = {
  budget: "The DE budget",
  subscriptions: "DE subscriptions",
  keys: "Ingest keys",
  settings: "DE settings",
  deploy: "The DE's deploy",
  channels: "The DE's channels",
  reporting: "The DE's KPI reporting",
};

/** Whole days since `started_on`, counting the start day as day 1. */
export function dayCounter(startedOn: string, today = new Date()): number {
  const start = Date.parse(`${startedOn}T00:00:00Z`);
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(1, Math.floor((now - start) / 86_400_000) + 1);
}

export function DeProfileScreen({
  api,
  company,
  id,
  tab,
  helper,
  onTab,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  tab: DeTab | undefined;
  /** HELPERS: the helper the Helpers tab scrolls to. */
  helper?: string | undefined;
  onTab: (tab: DeTab) => void;
}) {
  const kpi = useFeature("kpi");
  const agents = useFeature("agents");
  const chat = useFeature("chat");
  const meId = useMyMembershipId(company.id);
  const helperData = useHelpers(api, company.id);
  const helpers = helperData.byLead.get(id) ?? [];
  const { me } = useSession();
  const manager = isManager(company);
  const operator = company.roles.includes("agency_operator");
  const profile = useQuery(deQuery(api, company.id, id));
  const week = usePeriod();
  const dm = useOpenDm(api, company);
  // The scorecard keeps the audience `/kpis/de/{id}` had: the company's KPI readers (`cadence`;
  // a plain member only on its own pages). With `kpi` off they read "not enabled" there, as before.
  const cadence = rulesByKey("cadence");
  const scorecardReader = cadence
    ? !openOnly(cadence, company) && modeAccess(cadence, me, company) !== "role"
    : false;

  const de = profile.data;
  const available = DE_TABS.filter(
    (t) =>
      !SECTION_OF[t] &&
      (manager || !MANAGER_TABS.includes(t)) &&
      // WF-FIX: the composition (managed runtime profiles) is not a self-hosted DE's.
      (t !== "agents" || (operator && agents.enabled && !de?.self_hosted)) &&
      // D10: the policy routes are the `agents` add-on's (managers and the supervisor read)
      (t !== "permissions" || agents.enabled) &&
      (t !== "scorecard" || scorecardReader),
  );
  // A DE in setup opens on its checklist; a live one on its record (AGENT-RECORD, decision 2).
  // HELPERS (founder 2026-10-30): the Helpers tab is always there (none yet: "Ask for a helper").
  const asked: DeTab = tab ?? (de?.status === "onboarding" ? "overview" : "record");
  const current = sectionOf(asked);
  const name = de ? de.display_name : "Digital employee";
  // The tabs with at least one section this caller may open; the open one's sections.
  const areas = DE_AREAS.filter((a) =>
    (a.sections as readonly DeTab[]).some((t) => available.includes(t)),
  );
  const area = areaOf(current);
  const sections = (area.sections as readonly DeTab[]).filter((t) => available.includes(t));
  const pickArea = (key: string) => {
    const next = DE_AREAS.find((a) => a.key === key);
    const first = next?.sections.find((t) => available.includes(t));
    if (first) onTab(first);
  };
  // A section named in the URL scrolls into view once, when it is named (not again on a profile
  // update, Codex UX-B r1 #3); a tab's first section only when it is above the viewport.
  const ready = Boolean(de);
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!tab || !ready || scrolledFor.current === tab) return;
    scrolledFor.current = tab;
    const el = document.getElementById(`de-section-${sectionOf(tab)}`);
    if (el && (el.previousElementSibling || el.getBoundingClientRect().top < 0))
      el.scrollIntoView?.({ block: "start" });
  }, [tab, ready]);

  // The old scorecard URLs land here (`?tab=scorecard`): with the KPI add-on off a KPI reader
  // gets the not-enabled page they gave, whether or not the DE exists, never a blank page.
  if (tab === "scorecard" && !kpi.enabled && scorecardReader)
    return (
      <NotEnabledPage
        what="Scorecard"
        feature="kpi"
        companyName={company.name}
        slug={company.slug}
      />
    );
  if (profile.isError && profile.error instanceof ApiError && profile.error.status === 404)
    return <NotFoundPage message="No such digital employee in this company." />;
  // HELPERS-2 (Codex HELPERS): a viewer without access to the DE's seat (a Helpers link from the
  // Team list or a chart card) gets a clean "not available" page, not a load error, and the
  // helper they followed still opens on its own page.
  if (profile.isError && profile.error instanceof ApiError && profile.error.status === 403)
    return (
      <div className="flex min-w-0 flex-1 flex-col items-center gap-3" data-slot="de-no-access">
        <NoAccessPage what="This digital employee" slug={company.slug} />
        {helper ? (
          <Link
            to="/c/$slug/agents/ag/$id"
            params={{ slug: company.slug, id: helper }}
            search={keepFocus}
            className="text-sm text-primary-text hover:underline"
            data-slot="de-no-access-helper"
          >
            Open the helper
          </Link>
        ) : null}
      </div>
    );

  /** One section's body (only the open tab's sections mount). */
  const section = (t: DeTab): ReactNode => {
    switch (t) {
      case "record":
        return <RecordTab api={api} company={company} id={id} />;
      case "runs":
        return !agents.enabled ? (
          <DeRunsOff name={name} />
        ) : de ? (
          <DeRunsTab api={api} company={company} de={de} />
        ) : (
          <Loading label="Loading runs" />
        );
      case "approvals":
        return !agents.enabled ? (
          <DeApprovalsOff name={name} />
        ) : de ? (
          <DeApprovalsTab api={api} company={company} de={de} />
        ) : (
          <Loading label="Loading approvals" />
        );
      // DOCS-KB: base plan, every role that sees the profile; read-only with `agents` off.
      case "knowledge":
        return <KnowledgeTab api={api} company={company} membershipId={id} agentName={name} />;
      // REACH-S1: the Reach in force (the API decides who reads and who edits).
      case "reach":
        return <ReachSection api={api} company={company} membershipId={id} name={name} />;
      // D10 scope editor: per-class Direct / Ask / Never, the keys' scopes, the explainer.
      case "permissions":
        return de ? (
          <div className="flex min-w-0 flex-col gap-6">
            {/* TEMPLATES-T4: a role's probation and access (nothing for a DE without a role). */}
            {manager ? (
              <DeRoleAccess
                api={api}
                company={company}
                deId={de.membership_id}
                name={workerName(name)}
              />
            ) : null}
            {/* one editor per company and seat: a draft never carries to another agent (Codex r1 #3) */}
            <DePermissionsTab
              key={`${company.id}:${de.membership_id}:${de.seat?.id ?? "none"}`}
              api={api}
              company={company}
              de={de}
            />
          </div>
        ) : (
          <Loading label="Loading permissions" />
        );
      case "skills":
        // Skill packs are an `agents` route: off, one line and no call.
        return !agents.enabled ? (
          <DeSkillsOff name={name} />
        ) : de ? (
          <DeSkillsTab api={api} company={company} de={de} />
        ) : (
          <Loading label="Loading skills" />
        );
      case "connections":
        // UX pass 1.2: Connections & keys, one section (the ingest keys are the managers').
        return (
          <div className="flex min-w-0 flex-col gap-6">
            {de ? (
              <DeConnectionsTab api={api} company={company} de={de} />
            ) : (
              <Loading label="Loading connections" />
            )}
            {manager ? (
              de ? (
                <KeysTab api={api} companyId={company.id} de={de} />
              ) : (
                <Loading label="Loading keys" />
              )
            ) : null}
          </div>
        );
      case "budget":
        // UX pass 1.2: Models & budget (the edit screen's step) and the DE's own budget.
        return de ? (
          <div className="flex min-w-0 flex-col gap-6">
            <DeSetupPart api={api} company={company} de={de} part="models" />
            <DeBudgetTab api={api} company={company} de={de} />
          </div>
        ) : (
          <Loading label="Loading the budget" />
        );
      case "deploy":
      case "channels":
      case "reporting":
        return de ? (
          <DeSetupPart api={api} company={company} de={de} part={t} />
        ) : (
          <Loading label="Loading the set-up" />
        );
      case "scorecard":
        return (
          <div className="flex min-w-0 flex-col gap-3" data-slot="de-scorecard-week">
            <p className="text-xs text-muted-foreground">
              Week {week} · {weekRangeLabel(week)}
            </p>
            {!kpi.enabled ? (
              <NotEnabledPage
                what="Scorecard"
                feature="kpi"
                companyName={company.name}
                slug={company.slug}
              />
            ) : de?.seat ? (
              <ScorecardTab api={api} companyId={company.id} id={id} week={week} />
            ) : profile.isPending ? (
              <Loading label="Loading the scorecard" rows={6} />
            ) : (
              <SeatlessNote what="Scorecards" />
            )}
          </div>
        );
      case "agents":
        return !agents.enabled ? null : de ? (
          <DeAgentsTab api={api} company={company} de={de} />
        ) : (
          <Loading label="Loading the composition" />
        );
      case "profile":
        return de ? (
          <DeProfileSection api={api} company={company} de={de} />
        ) : (
          <Loading label="Loading the profile" rows={3} />
        );
      case "helpers":
        return de ? (
          <DeHelpersTab
            api={api}
            company={company}
            de={de}
            helpers={helpers}
            all={helperData.all}
            available={helperData.available}
            meId={meId}
            canMessage={chat.enabled && !chat.readOnly && meId !== null}
            now={helperData.now}
            focus={helper}
          />
        ) : (
          <Loading label="Loading helpers" rows={3} />
        );
      // UX pass 1.2: Time (was the Work area's) and Cost, both on the Activity tab.
      case "time":
        return de?.seat ? (
          <TimeTab api={api} company={company} seatId={de.seat.id} week={week} de={de} />
        ) : profile.isPending ? (
          <Loading label="Loading time entries" />
        ) : (
          <SeatlessNote what="Time entries" />
        );
      case "cost":
        return de?.seat ? (
          <CostTab api={api} company={company} seatId={de.seat.id} week={week} />
        ) : profile.isPending ? (
          <Loading label="Loading cost" />
        ) : (
          <SeatlessNote what="Cost" />
        );
      case "subscriptions":
        return de ? (
          <DeSubscriptionsTab api={api} company={company} de={de} />
        ) : (
          <Loading label="Loading subscriptions" />
        );
      case "settings":
        // The seat and template facts (the edit screen's first steps), then identity & runtime.
        return de ? (
          <div className="flex min-w-0 flex-col gap-6">
            <DeSetupPart api={api} company={company} de={de} part="facts" />
            <DeSettingsTab
              key={de.membership_id}
              api={api}
              companyId={company.id}
              de={de}
              operator={operator}
            />
          </div>
        ) : (
          <Loading label="Loading settings" />
        );
      // AGENT-MENTION: the job configuration "Edit" opens, and the KPI slot (KPI-ASSIGN). UX pass
      // 1.2: a manager edits it with the set-up's own editor (own standing orders before any
      // template, Deploy changes); the DE's supervisor with the profile's.
      case "job":
        // A manager waits for the profile rather than mounting the supervisor's editor first
        // (which read the standing orders a second time).
        return manager ? (
          de ? (
            <DeSetupPart api={api} company={company} de={de} part="job" />
          ) : (
            <Loading label="Loading the set-up" rows={3} />
          )
        ) : (
          <DeJobTab api={api} company={company} deId={id} name={name} />
        );
      case "kpis":
        return (
          <DeKpisSlot
            api={api}
            company={company}
            name={name}
            kpi={kpi.enabled}
            seat={de?.seat ? { id: de.seat.id, title: de.seat.title } : null}
            pending={profile.isPending}
            week={week}
          />
        );
      default:
        return de ? (
          <OverviewTab api={api} company={company} de={de} kpi={kpi.enabled} onTab={onTab} />
        ) : (
          <Loading label="Loading the overview" />
        );
    }
  };

  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="agent-profile">
      <LocationHeader
        crumbs={[
          {
            label: "Digital employees",
            render: (
              <Link to="/c/$slug/agents" params={{ slug: company.slug }} search={keepFocus} />
            ),
          },
        ]}
        title={
          <span className="inline-flex min-w-0 items-center gap-2">
            <AvatarTile name={workerName(name)} kind="agent" size="sm" aria-hidden="true" />
            <span className="truncate" data-slot="de-header-name">
              {deLabel(name, de?.person_name)}
            </span>
            <WorkerBadge kind="de" />
          </span>
        }
        badge={
          // WF-POLISH (Andrei, 2026-10-27): name, DE badge, role, live/idle, reports-to. Nothing
          // else: the runtime, template and deploy are the Overview's "Runtime" line.
          de ? (
            // Wave 22: one line that gives way before the name does (it wrapped into the
            // breadcrumb at 1280 px); the role shows from 1536 px (the Overview carries it too).
            <span className="inline-flex min-w-0 shrink-[100] flex-nowrap items-center gap-x-3 overflow-hidden text-xs">
              <span
                className="max-w-[28ch] truncate text-muted-foreground max-2xl:hidden"
                title={de.role ?? de.seat?.title ?? undefined}
                data-slot="de-header-role"
              >
                {de.role ?? de.seat?.title ?? "No role yet"}
              </span>
              {/* DE-WIZARD: readiness (Draft → … → Live / Not live, why on hover); WF-FIX:
                  Live only with a recent run or MCP call, else Idle. */}
              <DeLiveBadge de={de} />
              {/* TEMPLATES-T4: on probation at Draft level (a DE set up from a role). */}
              <DeProbationBadge de={de} />
              {de.removed_at ? (
                <Badge variant="danger" data-slot="de-removed">
                  Removed
                </Badge>
              ) : null}
              <span
                className="min-w-0 truncate text-muted-foreground"
                data-slot="de-header-reports-to"
              >
                Reports to{" "}
                <span className="text-foreground">
                  {de.supervisor
                    ? (de.supervisor.name ?? `${de.supervisor.title} (open)`)
                    : "no one"}
                </span>
                {/* STRUCTURE-CHANGED §3.1: the DE's seat moves through the chart's draft. */}
                {manager && de.seat && !de.removed_at ? (
                  <SeatReportsToButton api={api} company={company} seatId={de.seat.id} />
                ) : null}
              </span>
              {/* HELPERS: "14 helpers · 31 skills", only with helpers; opens the Helpers tab. */}
              {helpers.length ? (
                <button
                  type="button"
                  className="truncate text-muted-foreground hover:text-foreground hover:underline"
                  data-slot="de-header-helpers"
                  onClick={() => onTab("helpers")}
                >
                  {helpersSkillsText(helpers.length, skillTotal(de.role_skill_keys, helpers))}
                </button>
              ) : null}
            </span>
          ) : null
        }
        actions={
          de ? (
            <HeaderActions
              api={api}
              company={company}
              de={de}
              manager={manager}
              operator={operator}
              onEdit={() => onTab("job")}
              onMessage={() => dm.mutate(de.membership_id)}
              messaging={dm.isPending}
            />
          ) : null
        }
      />
      {agents.enabled ? null : <AgentsOffBanner company={company} />}
      <div className="flex min-w-0 flex-col gap-4 px-6 py-4 max-md:px-4">
        {profile.isPending ? (
          <Loading label="Loading the profile" rows={2} />
        ) : profile.isError ? (
          <LoadError what="The profile" error={profile.error} />
        ) : null}

        <ViewTabs
          className="min-w-0"
          aria-label="Profile areas"
          value={area.key}
          onValueChange={pickArea}
          tabs={areas.map((a) => ({ value: a.key, label: a.label }))}
        >
          <TabsContent value={area.key} className="min-w-0 pt-4" data-area={area.key}>
            {(MANAGER_TABS.includes(asked) || MANAGER_TABS.includes(current)) && !manager ? (
              <NoAccessPage
                what={MANAGER_TAB_NAMES[asked] ?? MANAGER_TAB_NAMES[current] ?? "This section"}
                slug={company.slug}
              />
            ) : current === "agents" && !operator ? (
              <NoAccessPage what="The DE's composition" slug={company.slug} />
            ) : current === "agents" && de?.self_hosted ? (
              // WF-FIX: a self-hosted DE has no managed composition (its Hermes runs elsewhere).
              <p className="text-sm text-muted-foreground" data-slot="no-composition">
                Self-hosted on Hermes: no composition here.
              </p>
            ) : current === "scorecard" && !scorecardReader ? (
              <NoAccessPage what="The DE scorecard" slug={company.slug} />
            ) : (
              <AreaPage
                sections={sections.map((t) => ({ key: t, label: SECTION_LABELS[t] }))}
                current={current}
              >
                {(t) => section(t)}
              </AreaPage>
            )}
            {area.key === "overview" && de ? (
              <div className="mt-8">
                <Runtime
                  de={de}
                  operator={operator}
                  kpi={kpi.enabled}
                  api={api}
                  companyId={company.id}
                />
              </div>
            ) : null}
          </TabsContent>
        </ViewTabs>
      </div>
    </div>
  );
}

/** Pause / Resume, Message, Edit, Open in AiGentPanel (operators) and the "…" menu. */
function HeaderActions({
  api,
  company,
  de,
  manager,
  operator,
  onEdit,
  onMessage,
  messaging,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
  manager: boolean;
  operator: boolean;
  onEdit: () => void;
  onMessage: () => void;
  messaging: boolean;
}) {
  const { moves, start, dialog } = useLifecycle(api, company.id, de);
  const toggle = moves.find((m) => m === "pause" || m === "resume");
  const Toggle = toggle ? MOVES[toggle].icon : null;
  // WORKFORCE: a person holding, supervising or above the DE's seat may pause / resume and
  // rename it (`can_intervene`); template, runtime, retire and remove stay the managers'.
  const intervene = manager || de.can_intervene === true;
  // DE-FOLLOWUPS: deploying a not-deployed DE is a manager's move (`mayChangeDe`).
  const mayToggle = de.status === "not_deployed" ? manager : intervene;
  const [renaming, setRenaming] = useState(false);
  // DE-BRIEFS: the DE's brief (rhythm, recipients), for whoever may intervene on it.
  const agents = useFeature("agents", company.id);
  const [briefing, setBriefing] = useState(false);
  return (
    <>
      {renaming ? (
        <RenameDeDialog api={api} company={company} de={de} onClose={() => setRenaming(false)} />
      ) : null}
      {briefing ? (
        <BriefSettingsModal
          api={api}
          co={company.id}
          target={{ scope: "de", id: de.membership_id, name: de.display_name }}
          onClose={() => setBriefing(false)}
        />
      ) : null}
      {mayToggle && toggle && Toggle ? (
        <Button variant="secondary" size="sm" onClick={() => start(toggle)}>
          <Toggle aria-hidden="true" />
          <span className="max-sm:sr-only">{MOVES[toggle].label}</span>
        </Button>
      ) : null}
      {/* WF-FIX (UX pass P0 0.7): put the DE in a channel, one click from its page. */}
      <AddToChannelButton api={api} company={company} de={de} lazy />
      {/* At phone width the buttons keep their icon and accessible name, not their label. */}
      <Button variant="secondary" size="sm" disabled={messaging} onClick={onMessage}>
        <MessageSquareIcon aria-hidden="true" />
        <span className="max-sm:sr-only">Message</span>
      </Button>
      {/* INBOX-PREFS: what of this DE reaches the person (All / Mentions / Mute). */}
      <NotifyLevelMenu api={api} co={company.id} subjectKind="agent" subjectId={de.membership_id} />
      {intervene && agents.enabled ? (
        <Button variant="secondary" size="sm" onClick={() => setBriefing(true)}>
          <MailIcon aria-hidden="true" />
          <span className="max-sm:sr-only">Brief</span>
        </Button>
      ) : null}
      {manager ? (
        <Button variant="secondary" size="sm" onClick={onEdit}>
          <PencilIcon aria-hidden="true" />
          <span className="max-sm:sr-only">Edit</span>
        </Button>
      ) : intervene ? (
        <Button variant="secondary" size="sm" onClick={() => setRenaming(true)}>
          <PencilIcon aria-hidden="true" />
          <span className="max-sm:sr-only">Rename</span>
        </Button>
      ) : null}
      {/* AGENT-MENTION: the whole lifecycle in view (managers): retire and remove, confirmed. */}
      {manager
        ? (["retire", "remove"] as const)
            .filter((m) => moves.includes(m))
            .map((m) => {
              const Icon = MOVES[m].icon;
              return (
                <Button
                  key={m}
                  variant="ghost"
                  size="sm"
                  data-move={m}
                  className="hover:text-danger-text"
                  aria-label={`${MOVES[m].label} ${de.display_name}`}
                  title={MOVES[m].label}
                  onClick={() => start(m)}
                >
                  <Icon aria-hidden="true" />
                  {/* Wave 22: icon only below 1536 px, so the header's name and status fit. */}
                  <span className="max-2xl:sr-only">{MOVES[m].label}</span>
                </Button>
              );
            })
        : null}
      {/* WF-FIX: not for a self-hosted DE (AiGentPanel does not run it; ClawPanel does). */}
      {operator && !de.self_hosted ? (
        <OpenAigentpanelButton api={api} companyId={company.id} deId={de.membership_id} />
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="ghost" size="icon-sm" aria-label="More actions" />}
        >
          <MoreHorizontalIcon aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          {/* UX pass 1.2: "Open scorecard" went: the scorecard is the KPIs tab. */}
          <DropdownMenuItem onClick={() => void copyLink(window.location.href)}>
            <LinkIcon aria-hidden="true" />
            Copy link
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog}
    </>
  );
}

/**
 * WF-FIX (audit "Copy link always toasts success"): copies, then says what happened — the
 * clipboard can be missing (an insecure origin) or refuse (no permission).
 */
export async function copyLink(href: string): Promise<boolean> {
  try {
    if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
    await navigator.clipboard.writeText(href);
    toast.add({ title: "Link copied", type: "success" });
    return true;
  } catch {
    toast.add({ title: "The link could not be copied", description: href, type: "error" });
    return false;
  }
}

/**
 * One tab of the DE page: its sections stacked on one full-width scrolling page, each with a
 * small heading (none for the Overview itself). WF-POLISH: no "On this page" rail.
 */
function AreaPage({
  sections,
  current,
  children,
}: {
  sections: { key: DeTab; label: string }[];
  current: DeTab;
  children: (tab: DeTab) => ReactNode;
}) {
  const body = sections.map((s) => (
    <section
      key={s.key}
      id={`de-section-${s.key}`}
      className="flex min-w-0 scroll-mt-4 flex-col gap-3"
      data-section={s.key}
      aria-label={s.label}
    >
      {s.key === "overview" ? null : (
        <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {s.label}
        </h2>
      )}
      <WhenNear eager={s.key === current} label={s.label}>
        {() => children(s.key)}
      </WhenNear>
    </section>
  ));
  return <div className="flex min-w-0 flex-col gap-8">{body}</div>;
}

function SeatlessNote({ what }: { what: string }) {
  return (
    <p className="text-sm text-muted-foreground">
      {what} are kept per seat, and this DE has no active seat.
    </p>
  );
}

/**
 * WORKFORCE: a self-hosted DE's runtime as its header says it — WF-FIX: "Hermes · <container> ·
 * <profile> · Live" — from its lead profile's recorded runtime (the one source: never the
 * managed `runtime_ref`); "Self-hosted on Hermes" when ops has not recorded where yet. Never
 * "not provisioned". Live is earned by activity (`liveness.tsx`): an active DE with no recent
 * run or MCP call reads "Idle".
 */
export function selfHostedLabel(
  de: Pick<DeProfile, "self_hosted" | "status"> &
    Partial<Pick<DeProfile, "readiness" | "keys" | "activity">>,
): string {
  const r = de.self_hosted?.runtime;
  const live =
    de.readiness && de.keys
      ? liveness({ readiness: de.readiness, keys: de.keys, activity: de.activity })
      : null;
  const state = live ? live.label : de.status === "active" ? "Active" : DE_STATUS_LABELS[de.status];
  return r
    ? ["Hermes", r.container, r.profile, state].join(" · ")
    : `Self-hosted on Hermes · ${state}`;
}

/**
 * What the DE runs, from the deploy mirror (ADR-0018 §4: written only by
 * `digital_employee.deployed`, after AiGentPanel confirmed the bundle and its sha matched). A
 * failed deploy leaves the mirror on the previous bundle, so this is always what is live.
 */
export function DeploymentFact({ de }: { de: DeProfile }) {
  // WORKFORCE: a self-hosted DE runs outside AiGentPanel (Hermes in a ClawPanel container); a
  // self-hosted DE never shows the managed deployment (deploy_ref / bundle_sha), with or without
  // a recorded runtime ("Self-hosted on Hermes" until ops records where).
  if (de.self_hosted)
    return (
      <span data-slot="de-deployment" data-state="self-hosted">
        {selfHostedLabel(de)}
      </span>
    );
  if (!de.deploy_ref || !de.bundle_sha)
    return (
      <span className="text-muted-foreground" data-slot="de-deployment" data-state="none">
        Not deployed through AiGentPanel
      </span>
    );
  const verified = de.verification_state === "verified";
  const pilot = de.verification_override;
  return (
    <span
      className="inline-flex flex-wrap items-center gap-2"
      data-slot="de-deployment"
      data-state="deployed"
    >
      {verified ? <Badge variant="success">Verified</Badge> : <UnverifiedBadge pilot={pilot} />}
      <span>
        Bundle{" "}
        <code className="font-mono text-xs" title={de.bundle_sha}>
          {de.bundle_sha.slice(0, 12)}
        </code>
      </span>
      {/* DC-05: why it is unverified — an operator's pilot override, and until when. */}
      {pilot ? (
        <span className="text-muted-foreground" data-slot="de-pilot">
          {pilotNote(pilot)} · {pilot.reason}
        </span>
      ) : null}
    </span>
  );
}

/**
 * AGENT-POLISH: the DE's AiGentPanel profile name (`northwind-recruiter-riley-3f9a`), its
 * instance once deployed, and for an agency operator a link into the panel (a one-time launch,
 * as the header's button).
 */
function PanelProfileFact({
  panel,
  api,
  companyId,
  deId,
}: {
  panel: NonNullable<DeProfile["aigentpanel"]>;
  api: Api | null;
  companyId: string;
  deId: string;
}) {
  return (
    <span
      className="inline-flex flex-wrap items-center gap-2"
      data-slot="de-panel-profile"
      data-state={panel.deployed ? "deployed" : "planned"}
    >
      <code className="font-mono text-xs">{panel.profile_name}</code>
      <span className="text-muted-foreground">
        {panel.deployed
          ? panel.instance_id
            ? `on ${panel.instance_id}`
            : "deployed"
          : "planned (set on the first deploy)"}
      </span>
      {api ? <PanelLink api={api} companyId={companyId} deId={deId} /> : null}
    </span>
  );
}

function PanelLink({ api, companyId, deId }: { api: Api; companyId: string; deId: string }) {
  const open = useOpenAigentpanel(api);
  return (
    <button
      type="button"
      className="text-primary-text underline-offset-4 hover:underline disabled:opacity-50"
      disabled={open.pending}
      onClick={() => open.open({ companyId, deId })}
      data-slot="de-panel-link"
    >
      Open in AiGentPanel
    </button>
  );
}

/**
 * WF-POLISH: the runtime as one line at the bottom of the Overview — where it runs in a few
 * words, every fact (template, started, deployment, AiGentPanel profile, band, managed runtime)
 * behind the (i). Was the facts line under the header.
 */
function Runtime({
  de,
  operator,
  kpi,
  api,
  companyId,
}: {
  de: DeProfile;
  operator: boolean;
  kpi: boolean;
  api: Api;
  companyId: string;
}) {
  const panel = de.aigentpanel;
  const items: { label: string; value: ReactNode }[] = [
    { label: "Seat", value: de.seat?.title ?? "No seat" },
    {
      label: "Reports to",
      value: de.supervisor ? `${de.supervisor.name ?? "Vacant"} · ${de.supervisor.title}` : "None",
    },
    { label: "Template", value: <code className="font-mono text-xs">{de.template_key}</code> },
    {
      label: "Started",
      value: de.started_on
        ? `${formatDate(de.started_on)} · day ${dayCounter(de.started_on)}`
        : "Not started",
    },
    {
      label: "LiteLLM key",
      value: de.litellm_key_alias ? (
        <code className="font-mono text-xs">{de.litellm_key_alias}</code>
      ) : (
        "Not issued"
      ),
    },
    // WF-FIX: a self-hosted DE's runtime is one fact ("Runs on"), from its lead profile.
    { label: de.self_hosted ? "Runs on" : "Deployment", value: <DeploymentFact de={de} /> },
    // AGENT-POLISH: the readable AiGentPanel profile (planned until the first hand-off); not
    // for a self-hosted DE, which AiGentPanel does not run (WF-FIX).
    ...(panel && !de.self_hosted
      ? [
          {
            label: "AiGentPanel",
            value: (
              <PanelProfileFact
                panel={panel}
                api={operator ? api : null}
                companyId={companyId}
                deId={de.membership_id}
              />
            ),
          },
        ]
      : []),
    ...(kpi && de.scorecard
      ? [
          {
            label: `Band ${de.scorecard.period_key}`,
            value: <BandBadge band={de.scorecard.band} />,
          },
        ]
      : []),
    // WF-FIX: the managed runtime_ref, for operators, only for a DE that is not self-hosted (a
    // self-hosted DE's runtime is the "Runs on" fact above: one source, no mismatch).
    ...(operator && !de.self_hosted
      ? [
          {
            label: "Runtime",
            value: de.runtime_ref ? (
              <code className="font-mono text-xs">
                {de.runtime_ref.system}:{de.runtime_ref.instance_id}/{de.runtime_ref.profile_name}
              </code>
            ) : (
              "Not provisioned"
            ),
          },
        ]
      : []),
  ];
  // DC-05: an unverified deploy still says so, in the line (WORKFORCE: not for self-hosted).
  const unverified = deployedUnverified(de) && !de.self_hosted;
  const summary = de.self_hosted
    ? selfHostedLabel(de)
    : de.deploy_ref
      ? `AiGentPanel${de.aigentpanel?.instance_id ? ` · ${de.aigentpanel.instance_id}` : ""}`
      : "Not deployed";
  return (
    <div className="flex min-w-0 flex-col gap-2" data-slot="de-identity">
      {unverified ? <UnverifiedBadge pilot={de.verification_override} /> : null}
      <RuntimeLine items={items} summary={summary} />
    </div>
  );
}
